import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Avatar,
  Button,
  Input,
  Spinner,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import {
  Subtract20Regular,
  Dismiss20Regular,
  Send20Regular,
} from '@fluentui/react-icons';
import { fetchMessages, sendMessage } from '../portal/services/portalApi';
import { apiFetch } from '../lib/apiFetch';
import { formatBusinessTime } from '../lib/businessTime';

const useStyles = makeStyles({
  chatWindow: {
    position: 'fixed',
    bottom: '0',
    width: '328px',
    height: '455px',
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('8px', '8px', '0', '0'),
    boxShadow: tokens.shadow16,
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1100,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.padding('12px', '16px'),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke2),
    gap: '12px',
  },
  headerInfo: {
    flex: 1,
    minWidth: 0,
  },
  headerName: {
    fontWeight: '600',
    fontSize: '15px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  headerStatus: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  headerActions: {
    display: 'flex',
    gap: '4px',
  },
  messagesArea: {
    flex: 1,
    overflowY: 'auto',
    ...shorthands.padding('16px'),
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  inputArea: {
    ...shorthands.padding('12px', '16px'),
    ...shorthands.borderTop('1px', 'solid', tokens.colorNeutralStroke2),
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  messageInput: {
    flex: 1,
  },
  bubbleRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  bubbleRowOutgoing: {
    alignItems: 'flex-end',
  },
  bubbleRowIncoming: {
    alignItems: 'flex-start',
  },
  bubble: {
    maxWidth: '80%',
    ...shorthands.padding('8px', '12px'),
    ...shorthands.borderRadius('16px'),
    fontSize: '13px',
    lineHeight: '1.4',
    wordBreak: 'break-word',
  },
  bubbleOutgoing: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    borderBottomRightRadius: '4px',
  },
  bubbleIncoming: {
    backgroundColor: tokens.colorNeutralBackground3,
    color: tokens.colorNeutralForeground1,
    borderBottomLeftRadius: '4px',
  },
  bubbleMeta: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    ...shorthands.padding('0', '4px'),
  },
  typingDots: {
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
    ...shorthands.padding('8px', '12px'),
  },
  dot: {
    width: '7px',
    height: '7px',
    borderRadius: '50%',
    backgroundColor: tokens.colorNeutralForeground3,
    animationName: {
      '0%, 60%, 100%': { transform: 'translateY(0)' },
      '30%': { transform: 'translateY(-6px)' },
    },
    animationDuration: '1.2s',
    animationIterationCount: 'infinite',
  },
  loadingArea: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});

interface ChatWindowProps {
  conversation: {
    id: number;
    name: string;
    avatar: string;
    status?: string;
    conversationId?: string;
  };
  position: number;
  onClose: () => void;
  onMinimize: () => void;
  onSend?: (conversationId: string, text: string) => Promise<any>;
  onFetchMessages?: (conversationId: string) => Promise<any>;
  outgoingSenderType?: string; // which sender.type to treat as outgoing (default: 'customer')
}

const POLL_INTERVAL = 3000;
const TYPING_POLL_INTERVAL = 2000;

const ChatWindow: React.FC<ChatWindowProps> = ({ conversation, position, onClose, onMinimize, onSend, onFetchMessages, outgoingSenderType = 'customer' }) => {
  const styles = useStyles();
  const rightOffset = 16 + (position * 344);
  const [messages, setMessages] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [typing, setTyping] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const messageIdsRef = useRef<Set<string>>(new Set());

  const doFetchMessages = (convId: string) =>
    onFetchMessages ? onFetchMessages(convId) : fetchMessages(convId);

  // Initial load
  useEffect(() => {
    if (!conversation.conversationId) return;
    setLoading(true);
    doFetchMessages(conversation.conversationId)
      .then(data => {
        const msgs = data.messages || [];
        setMessages(msgs);
        messageIdsRef.current = new Set(msgs.map((m: any) => m.id));
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [conversation.conversationId]);

  // Poll for new messages
  useEffect(() => {
    if (!conversation.conversationId) return;
    const interval = setInterval(async () => {
      try {
        const data = await doFetchMessages(conversation.conversationId!);
        const msgs = data.messages || [];
        const hasNew = msgs.some((m: any) => !messageIdsRef.current.has(m.id));
        if (hasNew) {
          setMessages(msgs);
          messageIdsRef.current = new Set(msgs.map((m: any) => m.id));
        }
      } catch {}
    }, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [conversation.conversationId]);

  // Poll typing status
  useEffect(() => {
    if (!conversation.conversationId) return;
    const convId = conversation.conversationId;
    const interval = setInterval(async () => {
      try {
        const r = await apiFetch(`/portal/messages/${convId}/typing`);
        const data = await r.json();
        setTyping(data.typing === true);
      } catch {}
    }, TYPING_POLL_INTERVAL);
    return () => clearInterval(interval);
  }, [conversation.conversationId]);

  // Scroll to bottom on new messages or typing change
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, typing]);

  const handleSend = useCallback(async () => {
    if (!inputText.trim() || !conversation.conversationId || sending) return;
    setSending(true);
    const text = inputText.trim();
    setInputText('');
    try {
      const sendFn = onSend
        ? (convId: string, t: string) => onSend(convId, t)
        : sendMessage;
      const data = await sendFn(conversation.conversationId!, text);
      if (data.message) {
        setMessages(prev => {
          messageIdsRef.current.add(data.message.id);
          return [...prev, data.message];
        });
      }
    } catch (err) {
      console.error(err);
      setInputText(text); // restore on failure
    } finally {
      setSending(false);
    }
  }, [inputText, conversation.conversationId, sending, onSend]);

  const formatTime = (ts: string) => formatBusinessTime(ts);

  return (
    <div className={styles.chatWindow} style={{ right: `${rightOffset}px` }}>
      {/* Header */}
      <div className={styles.header}>
        <Avatar name={conversation.name} size={36} color="colorful" />
        <div className={styles.headerInfo}>
          <div className={styles.headerName}>{conversation.name}</div>
          <div className={styles.headerStatus}>{conversation.status || 'Customer'}</div>
        </div>
        <div className={styles.headerActions}>
          <Button appearance="subtle" icon={<Subtract20Regular />} size="small" onClick={onMinimize} />
          <Button appearance="subtle" icon={<Dismiss20Regular />} size="small" onClick={onClose} />
        </div>
      </div>

      {/* Messages Area */}
      {loading ? (
        <div className={styles.loadingArea}><Spinner size="small" /></div>
      ) : (
        <div className={styles.messagesArea}>
          {messages.map(msg => {
            const isOutgoing = msg.sender?.type === outgoingSenderType;
            return (
              <div
                key={msg.id}
                className={`${styles.bubbleRow} ${isOutgoing ? styles.bubbleRowOutgoing : styles.bubbleRowIncoming}`}
              >
                <div className={`${styles.bubble} ${isOutgoing ? styles.bubbleOutgoing : styles.bubbleIncoming}`}>
                  {msg.text}
                </div>
                <div className={styles.bubbleMeta}>{msg.timestamp ? formatTime(msg.timestamp) : ''}</div>
              </div>
            );
          })}

          {/* Typing indicator */}
          {typing && (
            <div className={`${styles.bubbleRow} ${styles.bubbleRowIncoming}`}>
              <div className={`${styles.bubble} ${styles.bubbleIncoming}`}>
                <div className={styles.typingDots}>
                  <div className={styles.dot} style={{ animationDelay: '0s' }} />
                  <div className={styles.dot} style={{ animationDelay: '0.2s' }} />
                  <div className={styles.dot} style={{ animationDelay: '0.4s' }} />
                </div>
              </div>
            </div>
          )}

          <div ref={bottomRef} />
        </div>
      )}

      {/* Input Area */}
      <div className={styles.inputArea}>
        <Input
          className={styles.messageInput}
          placeholder="Type a message..."
          value={inputText}
          onChange={(_, d) => setInputText(d.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSend(); }}
          disabled={!conversation.conversationId}
        />
        <Button
          appearance="primary"
          icon={<Send20Regular />}
          size="small"
          onClick={handleSend}
          disabled={!inputText.trim() || !conversation.conversationId || sending}
        />
      </div>
    </div>
  );
};

export default ChatWindow;
